import { Keyboard } from "@capacitor/keyboard";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonHeader,
  IonInput,
  IonLabel,
  IonRow,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import { Link } from "@mui/material";
import { get } from "@src/appV2/api";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { PRIVACY_POLICY_URL } from "@src/appV2/redesign/constants/urls";
import {
  getHasOpenPlacementsNearbyApiUrl,
  hasOpenPlacementsNearbyResponseSchema,
} from "@src/appV2/redesign/PlacementCandidate/api/useGetHasOpenPlacementsNearby";
import { useIsSurfaceJobsEnabled } from "@src/appV2/redesign/Placements/hooks/useIsSurfaceJobsEnabled";
import { useIsSurfaceShiftsEnabled } from "@src/appV2/redesign/ShiftDiscovery/useIsSurfaceShiftsEnabled";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { FC, FormEvent, KeyboardEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import Alert from "./Alert";
import { updateAgentData } from "./api";
import { OnBoardingComponentProps } from "./model";
import { StyledIonPage } from "./style";
import { ErrorAlert } from "./types";
import { reloadAgentProfile } from "../../store/session";
import { ONBOARDING_SEGMENT_EVENT_NAMES } from "../constants/ONBOARDING_SEGMENT_EVENT_NAMES";
import { ONBOARDING_STAGES } from "../constants/ONBOARDING_STAGES";
import { logOnboardingError } from "../util/logging";
import { getOigState } from "../util/oig";
import { fireOnboardingSegmentEvent } from "../util/segment";
import "../style.scss";

const AgentSSN: FC<
  Omit<OnBoardingComponentProps, "nextStagePath"> & {
    nextStagePath: (isSurfaceShiftsEnabled: boolean, isSurfaceJobsFlowValid: boolean) => string;
  }
> = ({ nextStagePath }) => {
  const { showErrorToast } = useToast();
  const dispatch = useDispatch();
  const { openArticle } = useZendeskArticle();
  const { whySsnSupportLink } = useSupportLinks();
  const [updatingData, setUpdatingData] = useState<boolean>(false);
  const [errorAlert, setErrorAlert] = useState<ErrorAlert>({ hasError: false });

  const [ssn, setSSN] = useState<string | null>(null);

  const worker = useDefinedWorker();

  const maskedSSN = worker.socialSecurityNumber && `xxx-xx-${worker.socialSecurityNumber}`;

  const history = useHistory();
  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();
  const isSurfaceJobsEnabled = useIsSurfaceJobsEnabled();

  const onNext = async (event?: FormEvent | MouseEvent): Promise<void> => {
    event && event.preventDefault();
    setErrorAlert({ hasError: false });
    try {
      if (ssn !== maskedSSN) {
        if (!ssn?.match("^(?!666|000|9\\d{2})\\d{3}-(?!00)\\d{2}-(?!0{4})\\d{4}$")) {
          showErrorToast("SSN is invalid");
          return;
        }
        if (isCapacitorPlatform()) {
          Keyboard.hide();
        }
        setUpdatingData(true);

        const oig = getOigState();

        await updateAgentData({
          stage: ONBOARDING_STAGES.SSN,
          ssn: ssn.replace(/\D/g, ""),
          ...(oig?.ssnRequired && {
            validationId: oig.validationId,
            requestFurtherValidation: oig.ssnRequired,
          }),
        });

        fireOnboardingSegmentEvent(ONBOARDING_SEGMENT_EVENT_NAMES.ENTERED_SSN, {
          hcpId: worker.userId,
        });
      }
      dispatch(reloadAgentProfile());

      let isSurfaceJobsFlowValid = false;
      // Check if there are any placements nearby
      if (isSurfaceJobsEnabled) {
        const hasOpenPlacementsNearbyResponse = await get({
          url: getHasOpenPlacementsNearbyApiUrl(worker.userId),
          responseSchema: hasOpenPlacementsNearbyResponseSchema,
        });

        if (hasOpenPlacementsNearbyResponse.data.data.attributes.hasOpenPlacementsNearby) {
          isSurfaceJobsFlowValid = true;
        }
      }

      history.push(nextStagePath(isSurfaceShiftsEnabled, isSurfaceJobsFlowValid));
    } catch (error) {
      if (!error.response || !error.response.text || error.response.status >= 500) {
        logOnboardingError(ONBOARDING_STAGES.SSN, (error as Error).message, worker.userId);
        setErrorAlert({
          hasError: true,
          reason: error?.message,
        });
        return;
      }
      showErrorToast("Unexpected error occurred while saving ssn");
    } finally {
      setUpdatingData(false);
    }
  };

  const closeErrorModal = () => {
    setErrorAlert({ hasError: false });
  };

  //Function to format input field to SSN
  const formatToSSN = (value: string) => {
    return value
      .replace(/[^0-9x]/g, "")
      .replace(/^(\w{3})/, "$1-")
      .replace(/-(\w{2})/, "-$1-")
      .replace(/(\w)-(\w{4}).*/, "$1-$2");
  };

  const handleOnChange = (event: Event) => {
    const target = event.target as EventTarget;
    target["value"] = target["value"].replace(/[^0-9x-]/g, "");
    setSSN(target["value"]);
  };

  //To format the input only when the key pressed is not backspace and delete
  const handleKeyUp = (event: KeyboardEvent<HTMLIonInputElement>) => {
    let ssNo = event.target["value"] as string;
    if (event.key !== "Backspace" && event.key !== "Delete") {
      ssNo = formatToSSN(ssNo);
    }
    setSSN(ssNo);
  };

  //To prevent inputs which are not number
  const handleKeyPress = (event: KeyboardEvent<HTMLIonInputElement>) => {
    if (isNaN(Number(event.key))) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (maskedSSN) {
      setSSN(maskedSSN);
    }
  }, [maskedSSN]);

  return (
    <StyledIonPage className="onboarding-page">
      <IonHeader no-border className="onboarding-header">
        <IonToolbar className="onboarding-toolbar">
          <IonButtons slot="start">
            <IonBackButton text="" defaultHref="/home/agentAddress" mode="ios" color="dark" />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <Alert
          isOpen={errorAlert.hasError}
          reason={errorAlert?.reason}
          closeAlert={closeErrorModal}
        />

        <div className="signup-content content-layout">
          <div className="form-container">
            <form onSubmit={onNext}>
              <IonRow>
                <IonCol>
                  <div className="form-heading">
                    <h4>What is your SSN?</h4>
                  </div>

                  <div>
                    <IonLabel className="form-label">Social Security Number</IonLabel>
                    <IonInput
                      name="ssn"
                      className="form-input"
                      type="text"
                      onIonChange={handleOnChange}
                      onKeyUp={handleKeyUp}
                      onKeyPress={handleKeyPress}
                      value={ssn}
                      maxlength={11}
                      disabled={updatingData}
                      autofocus
                      required
                    />
                  </div>
                  <p className="ssn-text">
                    We store an encrypted version of your SSN. We provide it to government agencies
                    and background check agencies to ensure compliance with state and federal law;
                    and to facilities in response to a regulatory requirement or government
                    investigation (in accordance with the terms outlined in our{" "}
                    <a href={PRIVACY_POLICY_URL}>privacy policy</a>).{" "}
                    <Link
                      data-testid="ssn_support_link"
                      onClick={() => openArticle(whySsnSupportLink)}
                    >
                      Click here{" "}
                    </Link>
                    to learn more about why we need your SSN, and what we do to keep your personal
                    information secure.
                  </p>
                </IonCol>
              </IonRow>
            </form>
          </div>

          <div className="signupform-footer footer-container">
            <IonButton
              expand="block"
              size="large"
              class="ion-margin-top ion-margin-bottom continue-button"
              disabled={updatingData || ssn?.length !== 11}
              onClick={onNext}
            >
              Continue
              {updatingData && <IonSpinner slot="end" class="ion-margin-start" name="lines" />}
            </IonButton>
          </div>
        </div>
      </IonContent>
    </StyledIonPage>
  );
};

export { AgentSSN };
